/**
 * Product development templates — 5 documents.
 *
 * formula-ownership-agreement, formula-development-agreement, product-development-agreement,
 * formula-assignment-agreement, flavor-house-agreement.
 */
export const PRODUCT_DEVELOPMENT_TEMPLATES = [
  {
    id: "formula-ownership-agreement",
    docId: "formula-ownership-agreement",
    title: "FORMULA OWNERSHIP AGREEMENT",
    version: "1.0",
    lastReviewed: "2026-08-25",

    founderGuide: {
      useThisWhen:
        "Someone other than you has touched, or is about to touch, the formula — a formulator, a lab, a co-manufacturer's technologist — and you need one standalone document saying the formula and every change to it belongs to you. Use it before development starts, and again before a plant makes any adjustment to a formula that is already yours.",
      whyItMatters:
        "Ownership of the formula is what makes you a brand rather than a customer of one. The trap is rarely an outright ownership grab; it is the improvement clause. A plant adjusts a stabiliser to fix separation, or a lab reworks the acid balance so the flavour holds through shelf life, and a quiet sentence gives them ownership of that change. You still own the original. What you actually sell is the improved version, and it belongs to someone else. Every plant you approach afterwards is quoting on a formula you cannot fully hand them.",
      negotiateCarefully: [
        {
          point: "Ownership of improvements, however they arise",
          detail:
            "This is the clause that separates owning the formula on paper from owning the formula you sell. Cover changes made by their staff on their own initiative, scale-up adjustments, and substitutions made to solve a supply problem — not just work you commissioned and paid for.",
        },
        {
          point: "What 'the formula' is defined to include",
          detail:
            "A bill of materials with percentages is not a formula a second plant can run. The definition needs process parameters, order of addition, times and temperatures, fill conditions, analytical methods and the named suppliers behind each input.",
        },
        {
          point: "Coded and compounded ingredients",
          detail:
            "A formula that lists a flavour code rather than a supplier and specification is a pointer to someone else's property. Identify each one honestly, say who owns it, and pair the carve-out with a supply or specification arrangement instead of pretending it transferred.",
        },
        {
          point: "An express right to take it to another manufacturer",
          detail:
            "Ownership without a written right to disclose the full package to an alternative manufacturer under confidentiality invites an argument at the exact moment you want to leave.",
        },
        {
          point: "Survival and independence from the supply relationship",
          detail:
            "Ownership terms that live inside a co-packing agreement and expire with it hand the question back to the counterparty on exit. Keep this document standalone, and make it control over any conflicting ownership language elsewhere.",
        },
      ],
      redFlags: [
        "Any clause granting the other side ownership of, or a licence to, improvements and modifications.",
        "A formula document that identifies key inputs only by code.",
        "Ownership terms drafted to terminate with the supply or manufacturing agreement.",
        "Refusal to permit disclosure of the formula to an alternative manufacturer.",
        "A requirement that any reformulation be performed exclusively by the counterparty.",
        "A right to withhold the specification while any commercial dispute is open.",
      ],
      timeToComplete: "20 minutes to fill · attorney review before signing",
    },

    placeholders: [
      { token: "[EFFECTIVE DATE]", label: "Effective date", example: "September 1, 2026" },
      { token: "[COMPANY NAME]", label: "Your legal entity name", brandKey: "legalEntityName", example: "Acme Beverage Co., LLC" },
      { token: "[COMPANY STATE]", label: "Your state of formation", brandKey: "stateOfFormation", example: "Delaware" },
      { token: "[COMPANY ENTITY TYPE]", label: "Your entity type", example: "limited liability company" },
      { token: "[COMPANY ADDRESS]", label: "Your address", brandKey: "principalAddress", example: "123 Main St, Austin, TX 78701" },
      { token: "[COUNTERPARTY NAME]", label: "Other party's legal name", example: "Midwest Co-Packing, Inc." },
      { token: "[COUNTERPARTY STATE]", label: "Other party's state of formation", example: "Ohio" },
      { token: "[COUNTERPARTY ENTITY TYPE]", label: "Other party's entity type", example: "corporation" },
      { token: "[COUNTERPARTY ADDRESS]", label: "Other party's address", example: "500 Industrial Way, Columbus, OH 43215" },
      { token: "[BRAND NAME]", label: "Your brand name", brandKey: "tradeName", example: "Acme" },
      { token: "[PRODUCT NAME]", label: "Product the formula makes", example: "Acme Sparkling Yuzu, 12 oz can" },
      { token: "[PRODUCT CATEGORY]", label: "Product category", brandKey: "productCategory", example: "functional beverage" },
      { token: "[RELATIONSHIP DESCRIPTION]", label: "What the other party does for you", example: "contract manufacturing and packaging services" },
      { token: "[FORMULA NAME]", label: "Internal name or number of the formula", example: "AC-YUZU-03" },
      { token: "[FORMULA VERSION]", label: "Current formula version", example: "3.2" },
      { token: "[FORMULA DATE]", label: "Date of the current version", example: "July 14, 2026" },
      { token: "[EXCLUDED COMPONENTS]", label: "Third-party proprietary components, or 'none'", example: "Flavor system code 4471 supplied by Northline Flavors" },
      { token: "[DELIVERY DAYS]", label: "Days to deliver the specification after a request", example: "10 business days" },
      { token: "[SPECIFICATION REFRESH PERIOD]", label: "How often the specification is refreshed", example: "12 months" },
      { token: "[NON-USE YEARS]", label: "Years the use restriction runs after termination", example: "5" },
      { token: "[GOVERNING LAW STATE]", label: "Governing law state", example: "Delaware" },
      { token: "[VENUE]", label: "Venue for disputes", example: "Wilmington, Delaware" },
    ],

    body: `This Formula Ownership Agreement (this "Agreement") is entered into as of [EFFECTIVE DATE]
(the "Effective Date") by and between:

  [COMPANY NAME], a [COMPANY STATE] [COMPANY ENTITY TYPE], with a principal place of business
  at [COMPANY ADDRESS] (the "Brand"); and

  [COUNTERPARTY NAME], a [COUNTERPARTY STATE] [COUNTERPARTY ENTITY TYPE], with a principal
  place of business at [COUNTERPARTY ADDRESS] (the "Other Party").

RECITALS

A. The Brand sells or intends to sell [PRODUCT NAME] under the [BRAND NAME] brand.

B. The Other Party provides or has provided [RELATIONSHIP DESCRIPTION] to the Brand in
   connection with that product, and in doing so has or will have access to the Formula and the
   Specification defined below.

C. The parties wish to record, in a standalone document, who owns the Formula, the Specification
   and every change made to either of them. This Agreement is independent of the commercial terms
   of any development, manufacturing, supply or other agreement between the parties.

1. DEFINITIONS

1.1 "Formula" means the complete formulation for [PRODUCT NAME], identified as [FORMULA NAME],
    version [FORMULA VERSION], dated [FORMULA DATE], together with every earlier and later
    version of it, including: the identity of each ingredient; the supplier, grade and part
    number of each ingredient; usage rates and tolerances; and the order of addition.

1.2 "Specification" means everything a competent manufacturer would need in order to produce the
    product to the Formula, including process parameters such as times, temperatures, pressures,
    shear, hold conditions and fill conditions; equipment assumptions; in-process checks;
    analytical methods and test protocols; finished-product parameters; packaging and fill
    parameters; and the stability and shelf-life data in the Other Party's possession that
    supports the Formula.

1.3 "Improvements" means any modification, adjustment, optimisation, scale-up change,
    substitution, derivative or new version of the Formula or the Specification, however it
    arises and whoever makes it. Improvements include changes made by the Other Party's own
    personnel on their own initiative and changes made to solve a production, stability, sensory,
    cost, allergen or supply problem, whether or not the Brand asked for them and whether or not
    the Brand paid for them.

1.4 "Excluded Components" means the third-party proprietary components identified as
    [EXCLUDED COMPONENTS], which are owned by their respective suppliers and are dealt with in
    Section 7. If no component is identified, there are no Excluded Components.

1.5 "Formula Materials" means the Formula, the Specification and all Improvements, together with
    all bench notes, trial records, batch records, sensory results, analytical data and retained
    samples relating to any of them.

2. OWNERSHIP

2.1 As between the parties, the Brand exclusively owns the Formula Materials and all intellectual
    property rights in them, including trade secret rights and rights in any documentation
    embodying them.

2.2 The Other Party acquires no ownership interest in any Formula Materials by developing,
    documenting, modifying, storing, producing to, testing or improving them. No ownership
    interest arises from the Other Party bearing any part of the cost of any development,
    trial or scale-up work.

2.3 Nothing in this Agreement transfers to the Brand any of the Other Party's pre-existing
    general manufacturing know-how, equipment, or process technology that is not specific to the
    Formula, provided that the Other Party's use of that know-how does not entitle it to any
    interest in the Formula Materials.

3. PRESENT ASSIGNMENT

3.1 The Other Party hereby irrevocably assigns to the Brand all right, title and interest it has
    or may later acquire in the Formula Materials, whether arising before or after the Effective
    Date.

3.2 The assignment in Section 3.1 takes effect on creation of the relevant material without any
    further act. Where any right cannot be assigned, the Other Party grants the Brand an
    exclusive, perpetual, irrevocable, worldwide, fully paid licence to use, reproduce, modify,
    disclose and have products made under that right, with the right to sublicense.

3.3 The Other Party shall ensure that every employee, contractor and consultant who contributes
    to any Formula Materials is bound by written obligations sufficient to give effect to this
    Section 3, and shall provide evidence of those obligations on request.

4. RESTRICTIONS ON USE

4.1 The Other Party shall not, except as expressly directed by the Brand in writing:
    (a) use any Formula Materials for its own account or for any person other than the Brand;
    (b) produce, or cause or permit any person to produce, any product made to or derived from
        the Formula for any person other than the Brand or a party the Brand designates;
    (c) replicate, adapt, reverse engineer or analyse any Formula Materials, or any product made
        to the Formula, for the purpose of creating a comparable product;
    (d) sell, transfer or dispose of any product made to the Formula, including trial material,
        overruns and non-conforming material, to any person other than the Brand or its designee;
    (e) disclose any Formula Materials to any person other than those permitted by Section 8; or
    (f) claim, register or assert any ownership interest in any Formula Materials.

4.2 The restrictions in Section 4.1 apply during any relationship between the parties and for
    [NON-USE YEARS] years after the last of those relationships ends. During that same period the
    Other Party shall not use any Formula Materials to develop, formulate, produce or sell any
    [PRODUCT CATEGORY] product for itself or for any third party.

4.3 Obligations with respect to any Formula Materials that constitute a trade secret continue for
    as long as that material remains a trade secret.

5. DELIVERY OF THE SPECIFICATION

5.1 Within [DELIVERY DAYS] of a written request from the Brand, the Other Party shall deliver to
    the Brand a complete and current copy of the Formula and the Specification, in a form that a
    competent manufacturer could use to produce the product without further explanation, together
    with the records described in Section 9.

5.2 The Other Party shall deliver an updated Formula and Specification at least once every
    [SPECIFICATION REFRESH PERIOD] whether or not requested.

5.3 The Other Party shall notify the Brand in writing before implementing any Improvement, and
    shall deliver the revised Formula and Specification promptly after the Improvement is made.

5.4 The Other Party shall not withhold, condition or delay delivery under this Section 5 on
    account of any dispute between the parties, and shall not assert any lien or retention right
    over any Formula Materials.

6. RIGHT TO TRANSFER PRODUCTION

6.1 The Brand may disclose the Formula Materials to any other manufacturer, laboratory, supplier,
    consultant, testing house, investor or acquirer, under confidentiality obligations of the
    Brand's choosing.

6.2 The Other Party shall not object to, interfere with, or seek to condition any such
    disclosure, and shall not contact any alternative manufacturer regarding the Formula
    Materials without the Brand's prior written consent.

6.3 On the Brand's request, the Other Party shall provide reasonable technical transfer
    assistance to an alternative manufacturer, at the Other Party's then-current rates for
    technical services, for a reasonable period following any request.

7. THIRD-PARTY COMPONENTS

7.1 The Other Party represents that the Excluded Components are the only components of the
    Formula owned by a third party, and that all other components and all process elements of the
    Formula and Specification may be assigned and used as set out in this Agreement.

7.2 For each Excluded Component, the Other Party shall identify in writing the supplier, the
    supplier's code or part number, the function of the component, the usage rate, and any
    allergen, certification and characterisation information it holds.

7.3 This Agreement does not purport to assign ownership of any Excluded Component. The Other
    Party shall cooperate with the Brand, at the Brand's request, in the Brand establishing a
    direct supply, specification or escrow arrangement with the owner of any Excluded Component.

8. CONFIDENTIALITY AND NON-USE

8.1 The Other Party shall hold all Formula Materials in confidence, protect them with at least
    the degree of care it uses for its own most sensitive confidential information, and in no
    event less than reasonable care.

8.2 The Other Party may disclose Formula Materials only to those of its employees and contractors
    who need them to perform for the Brand, who are bound by written confidentiality and non-use
    obligations at least as protective as this Section 8, and shall remain responsible for their
    compliance. The Other Party shall maintain a written list of persons with access and provide
    it to the Brand on request.

8.3 If the Other Party is required by law or legal process to disclose any Formula Materials, it
    shall, to the extent permitted, give the Brand prompt written notice so the Brand may seek
    protective relief, and shall disclose only the portion required.

9. RECORDS, SAMPLES AND RETURN

9.1 The Other Party shall maintain complete records of all work performed on the Formula
    Materials, including bench notes, trial data, batch records and analytical results, and shall
    make them available to the Brand on request.

9.2 On termination of the parties' commercial relationship, or on the Brand's written request,
    the Other Party shall deliver to the Brand all Formula Materials in its possession and shall
    then destroy any remaining copies, other than one archival copy retained solely for legal or
    quality record-keeping purposes, which copy remains subject to this Agreement.

10. WARRANTIES OF THE OTHER PARTY

10.1 The Other Party represents and warrants that: (a) it has the right and authority to enter
     into this Agreement and to make the assignment in Section 3; (b) other than the Excluded
     Components, it is not aware of any third-party right in any Formula Materials; and (c) no
     person who contributed to the Formula Materials on its behalf is subject to an obligation to
     a former employer or other party that conflicts with this Agreement.

11. SURVIVAL AND INDEPENDENCE

11.1 This Agreement survives the expiry or termination of any development, manufacturing,
     co-packing, supply, consulting or other agreement between the parties, for any reason.

11.2 The Brand's ownership under this Agreement is not conditioned on continued production, on
     any minimum volume, on the continuation of any commercial relationship, or on the
     performance of any other agreement.

11.3 If any other agreement between the parties addresses ownership of the Formula, the
     Specification or any Improvement, this Agreement controls to the extent of the conflict.

12. REMEDIES

12.1 The Other Party acknowledges that a breach of Sections 4, 5, 6, 8 or 9 may cause harm for
     which monetary damages alone may be an inadequate remedy, and that the Brand may seek
     equitable relief in addition to any other remedy available to it.

13. GOVERNING LAW AND VENUE

13.1 This Agreement is governed by the laws of the State of [GOVERNING LAW STATE], without regard
     to its conflict of laws principles. The parties consent to venue in [VENUE].

14. GENERAL

14.1 This Agreement is the entire agreement between the parties on its subject matter and may be
     amended only in a writing signed by both parties. The Brand may assign this Agreement,
     including to a successor in connection with a merger, financing or sale of substantially all
     assets, without the Other Party's consent. The Other Party may not assign it without the
     Brand's prior written consent. If any provision is held unenforceable, the remainder
     continues in effect. This Agreement may be signed in counterparts, including by electronic
     signature, each of which is an original.

IN WITNESS WHEREOF, the parties have executed this Agreement as of the Effective Date.


[COMPANY NAME]                            [COUNTERPARTY NAME]


By: _______________________________       By: _______________________________

Name: _____________________________       Name: _____________________________

Title: ____________________________       Title: ____________________________

Date: _____________________________       Date: _____________________________
`,
  },

  {
    id: "formula-development-agreement",
    docId: "formula-development-agreement",
    title: "FORMULA DEVELOPMENT AGREEMENT",
    version: "1.0",
    lastReviewed: "2026-08-25",

    founderGuide: {
      useThisWhen:
        "You are paying someone to create a formula — an individual food scientist, a development lab, a co-manufacturer's in-house team, or a flavour house extending into full formulation. Sign it before bench work starts, not after the first sample arrives.",
      whyItMatters:
        "The deliverable is the whole argument. A formula handed over as a bench recipe with percentages is not the same asset as a formula handed over with named suppliers, usage rates, process parameters, analytical methods and a cost per unit at your real volumes. The first cannot be given to a second manufacturer; the second can. Founders pay the development fee, receive the first version, and discover later that what they own is not what they need to move plants or sell the business.",
      negotiateCarefully: [
        {
          point: "What the delivered formula package actually contains",
          detail:
            "List it item by item in the agreement: ingredient identities with named suppliers and part numbers, usage rates and tolerances, order of addition, process parameters, analytical methods, a costed build at your stated volumes, and the label impact of the formulation.",
        },
        {
          point: "Acceptance, and which run it is tested against",
          detail:
            "Acceptance on a bench sample is acceptance of something that has never been made on a filling line. Tie the final milestone to performance at a pilot or first commercial run, and give yourself a review window long enough to actually run it.",
        },
        {
          point: "Ownership of iterations and later modifications",
          detail:
            "The version you sell is almost never the version delivered. Ownership needs to reach every iteration, rework and scale-up adjustment, whoever makes it and whether or not you paid separately for it.",
        },
        {
          point: "Non-use across the developer's other clients",
          detail:
            "A developer free to reuse your formulation approach can hand a close cousin of your product to the next brand through the door. Ask for a use restriction in your category for a defined period, not just a secrecy obligation.",
        },
        {
          point: "Payment shape and how much is left at the end",
          detail:
            "If the last milestone is small, you have no leverage when the formula fails at scale. Hold back a meaningful portion until acceptance, and agree in advance how many rework rounds are inside the original fee.",
        },
      ],
      redFlags: [
        "Deliverables described only as 'the formula', with no package definition.",
        "Silence on ownership of iterations and later modifications.",
        "Full payment due on approval of a bench sample.",
        "No express right to disclose the formula to another manufacturer.",
        "A developer retaining the right to reuse the formulation for other clients.",
        "Ownership that transfers only on payment of amounts that are themselves in dispute.",
      ],
      timeToComplete: "30 minutes to fill · 1 to 2 weeks to agree scope and acceptance",
    },

    placeholders: [
      { token: "[EFFECTIVE DATE]", label: "Effective date", example: "September 1, 2026" },
      { token: "[COMPANY NAME]", label: "Your legal entity name", brandKey: "legalEntityName", example: "Acme Beverage Co., LLC" },
      { token: "[COMPANY STATE]", label: "Your state of formation", brandKey: "stateOfFormation", example: "Delaware" },
      { token: "[COMPANY ENTITY TYPE]", label: "Your entity type", example: "limited liability company" },
      { token: "[COMPANY ADDRESS]", label: "Your address", brandKey: "principalAddress", example: "123 Main St, Austin, TX 78701" },
      { token: "[DEVELOPER NAME]", label: "Developer's legal name", example: "Harbor Food Science LLC" },
      { token: "[DEVELOPER STATE]", label: "Developer's state of formation", example: "Illinois" },
      { token: "[DEVELOPER ENTITY TYPE]", label: "Developer's entity type", example: "limited liability company" },
      { token: "[DEVELOPER ADDRESS]", label: "Developer's address", example: "88 Lake St, Chicago, IL 60601" },
      { token: "[BRAND NAME]", label: "Your brand name", brandKey: "tradeName", example: "Acme" },
      { token: "[PRODUCT DESCRIPTION]", label: "Product to be developed", example: "a shelf-stable sparkling yuzu beverage in a 12 oz can" },
      { token: "[PRODUCT CATEGORY]", label: "Product category", brandKey: "productCategory", example: "functional beverage" },
      { token: "[TARGET SPECIFICATION]", label: "Target specification reference or summary", example: "Target Spec v2, dated August 10, 2026" },
      { token: "[TARGET COST]", label: "Target cost per unit", example: "0.42 per finished can" },
      { token: "[TARGET VOLUME]", label: "Volume the cost is built at", example: "50,000 cans per run" },
      { token: "[INTENDED CLAIMS]", label: "Claims the formula must support", example: "no added sugar; vegan; gluten free" },
      { token: "[DEVELOPMENT ROUNDS]", label: "Number of development rounds included", example: "3" },
      { token: "[SAMPLE QUANTITY]", label: "Samples delivered per round", example: "12 units" },
      { token: "[DEVELOPMENT PERIOD]", label: "Target period for development", example: "16 weeks from the Effective Date" },
      { token: "[ACCEPTANCE PERIOD]", label: "Days to evaluate a deliverable", example: "20 business days" },
      { token: "[ACCEPTANCE RUN]", label: "Run the final formula is accepted against", example: "a pilot run at the Brand's nominated co-manufacturer" },
      { token: "[TOTAL FEE]", label: "Total development fee", example: "24,000" },
      { token: "[KICKOFF FEE]", label: "Amount due at kickoff", example: "8,000" },
      { token: "[MIDPOINT FEE]", label: "Amount due at sample acceptance", example: "8,000" },
      { token: "[ACCEPTANCE FEE]", label: "Amount due at final acceptance", example: "8,000" },
      { token: "[PAYMENT DAYS]", label: "Payment terms in days", example: "30 days" },
      { token: "[REWORK ROUNDS]", label: "Rework rounds included in the fee", example: "2" },
      { token: "[EXCLUDED COMPONENTS]", label: "Third-party proprietary components, or 'none'", example: "Flavor system code 4471 supplied by Northline Flavors" },
      { token: "[NON-USE YEARS]", label: "Years the category use restriction runs", example: "3" },
      { token: "[GOVERNING LAW STATE]", label: "Governing law state", example: "Delaware" },
      { token: "[VENUE]", label: "Venue for disputes", example: "Wilmington, Delaware" },
    ],

    body: `This Formula Development Agreement (this "Agreement") is entered into as of [EFFECTIVE DATE]
(the "Effective Date") by and between:

  [COMPANY NAME], a [COMPANY STATE] [COMPANY ENTITY TYPE], with a principal place of business
  at [COMPANY ADDRESS] (the "Brand"); and

  [DEVELOPER NAME], a [DEVELOPER STATE] [DEVELOPER ENTITY TYPE], with a principal place of
  business at [DEVELOPER ADDRESS] (the "Developer").

1. ENGAGEMENT AND SCOPE

1.1 The Brand engages the Developer to develop a formula for [PRODUCT DESCRIPTION], to be sold
    under the [BRAND NAME] brand (the "Project").

1.2 The Developer shall perform the Project in accordance with the target specification described
    as [TARGET SPECIFICATION], which covers the intended sensory profile, nutritional profile,
    format and stability requirements (the "Target Specification").

1.3 The formula must be capable of being produced at a cost of [TARGET COST] at a volume of
    [TARGET VOLUME], using ingredients available at that volume, and must support the following
    intended claims: [INTENDED CLAIMS]. A formula that meets the sensory targets but not the cost
    or claims requirements has not met the Target Specification.

1.4 The Developer shall use qualified personnel and shall perform the Project with the skill and
    care ordinarily expected of a professional formulator working in the [PRODUCT CATEGORY]
    category.

2. DEVELOPMENT ROUNDS AND SAMPLES

2.1 The fee in Section 6 includes [DEVELOPMENT ROUNDS] rounds of development. Each round consists
    of a formulation iteration, delivery of [SAMPLE QUANTITY] samples, a written summary of what
    changed and why, and a review session with the Brand.

2.2 The Developer shall target completion of the Project within [DEVELOPMENT PERIOD] and shall
    notify the Brand promptly if that target is at risk, identifying the cause and any dependency
    on the Brand or on a third party.

2.3 Samples remain the property of the Brand. The Developer shall not supply samples of any
    formulation developed under this Agreement to any person other than the Brand and parties the
    Brand designates in writing.

3. THE DELIVERABLE FORMULA PACKAGE

3.1 On completion, the Developer shall deliver a complete formula package (the "Formula Package")
    containing, at minimum:
    (a) each ingredient by identity, grade, named supplier and supplier part number;
    (b) usage rates, tolerances and the order of addition;
    (c) process parameters, including mixing, heat, hold, shear, pH, fill and packaging
        conditions, and any equipment assumptions;
    (d) in-process checks, analytical methods and finished-product parameters;
    (e) a costed build at [TARGET VOLUME], showing ingredient cost per unit;
    (f) the ingredient statement in declaration order, the allergen position, and a written
        statement of which of the intended claims the formula supports;
    (g) known ingredient minimum order quantities and lead times; and
    (h) bench records, trial data and analytical results generated during the Project.

3.2 A deliverable that omits any element of Section 3.1 is incomplete and does not start the
    acceptance period in Section 4.

4. ACCEPTANCE

4.1 The Brand has [ACCEPTANCE PERIOD] after delivery of a deliverable to evaluate it against the
    Target Specification and to notify the Developer in writing of acceptance or of the respects
    in which it does not conform.

4.2 The final Formula Package is accepted only when the formula performs to the Target
    Specification at [ACCEPTANCE RUN]. Acceptance of a bench sample is not acceptance of the
    Formula Package.

4.3 If the Brand notifies non-conformance, the Developer shall correct the deliverable and
    redeliver it, and the acceptance period runs again from redelivery. The fee in Section 6
    includes [REWORK ROUNDS] such corrections.

4.4 Acceptance does not waive any warranty and does not limit any remedy for a defect that could
    not reasonably have been identified during the acceptance period.

5. STABILITY, TESTING AND REGULATORY RESPONSIBILITY

5.1 Unless the parties agree otherwise in writing, stability, shelf-life and microbiological
    testing are outside the scope of this Agreement, and the party responsible for arranging and
    paying for that testing is the Brand.

5.2 The Developer is responsible for providing accurate ingredient identities, usage rates,
    allergen information and supplier documentation in its possession, and for flagging to the
    Brand any ingredient it believes may affect the Brand's intended claims or label statements.

5.3 The Brand is responsible for decisions about labelling, claims, marketing statements and
    which market the product is sold into, and for obtaining its own review of those matters. The
    Developer is not responsible for those decisions, and the Brand is not entitled to rely on the
    Developer as its adviser on them.

5.4 Each party shall promptly notify the other of any information that comes to its attention
    suggesting an ingredient in the formula is unsuitable for the intended use.

6. FEES AND PAYMENT

6.1 The total fee for the Project is [TOTAL FEE], payable as follows:
    (a) [KICKOFF FEE] on execution of this Agreement;
    (b) [MIDPOINT FEE] on the Brand's acceptance of a sample under Section 4.1; and
    (c) [ACCEPTANCE FEE] on acceptance of the Formula Package under Section 4.2.

6.2 Invoices are payable within [PAYMENT DAYS] of receipt.

6.3 Ingredient purchases, pilot materials, third-party laboratory charges, plant time and travel
    are outside the fee and are payable by the Brand only where approved by the Brand in writing
    in advance.

7. OWNERSHIP

7.1 As between the parties, the Brand exclusively owns the formula, the Formula Package, all
    iterations, rejected versions, partial work, and every modification, improvement, scale-up
    adjustment, substitution and derivative of any of them, however arising and whoever makes
    them (together, the "Work Product"), and all intellectual property rights in them.

7.2 The Developer hereby irrevocably assigns to the Brand all right, title and interest it has or
    may acquire in the Work Product, effective on creation. Where any right cannot be assigned,
    the Developer grants the Brand an exclusive, perpetual, irrevocable, worldwide, fully paid
    licence to use, modify, disclose and have products made under it, with the right to
    sublicense.

7.3 The Developer retains ownership of its pre-existing methods, tools and general know-how not
    specific to the Work Product. Where any of it is embedded in a deliverable, the Developer
    grants the Brand a perpetual, irrevocable, worldwide, fully paid, sublicensable licence to use
    it as part of the Work Product.

7.4 The Excluded Components are [EXCLUDED COMPONENTS]. Those components are owned by their
    respective suppliers, are not assigned by this Agreement, and must be identified in the
    Formula Package with supplier, code, function and usage rate. The Developer shall cooperate
    with the Brand in the Brand establishing a direct arrangement with the owner of each one.

7.5 The Brand may disclose the Work Product to any manufacturer, laboratory, supplier, investor
    or acquirer under confidentiality obligations of the Brand's choosing. The Developer shall not
    object to or interfere with any such disclosure.

7.6 Ownership under this Section 7 is not conditional on payment of any disputed amount. The
    Developer may pursue unpaid fees as a debt, and shall not withhold the Work Product or assert
    any lien over it.

8. CONFIDENTIALITY AND NON-USE

8.1 The Developer shall hold the Work Product, the Target Specification, the Brand's product
    plans and all other non-public information of the Brand in confidence, and shall not disclose
    them other than to personnel who need them for the Project and who are bound by written
    obligations at least as protective as this Section 8.

8.2 During the Project and for [NON-USE YEARS] years afterwards, the Developer shall not use the
    Work Product, or any formulation approach derived from it, to develop, formulate or produce
    any [PRODUCT CATEGORY] product for itself or for any third party.

8.3 Obligations with respect to any information that constitutes a trade secret continue for as
    long as it remains a trade secret.

9. WARRANTIES

9.1 The Developer represents and warrants that: (a) it has the right and authority to enter into
    this Agreement and to make the assignment in Section 7; (b) the Work Product is original to it
    other than the Excluded Components; (c) no person contributing to the Work Product on its
    behalf is subject to a conflicting obligation to a former employer or other party; and (d) the
    Formula Package accurately records the formula as developed.

9.2 If the delivered formula fails to meet the Target Specification, the Developer shall correct
    it at its own cost within a reasonable period. If it cannot be corrected, the Brand may
    terminate and recover the fees paid for the affected phase.

10. TERM AND TERMINATION

10.1 This Agreement begins on the Effective Date and continues until the Project is completed or
     terminated.

10.2 Either party may terminate on written notice if the other materially breaches and does not
     cure within a reasonable period after notice. The Brand may terminate for convenience on
     written notice, in which case the Brand pays for work properly performed to the date of
     termination and nothing further.

10.3 On any termination, the Developer shall promptly deliver to the Brand all Work Product then
     in existence, including partial and rejected work, in the most complete form available.

10.4 Sections 3, 7, 8, 9, 11 and 12 survive termination or expiry.

11. GOVERNING LAW AND VENUE

11.1 This Agreement is governed by the laws of the State of [GOVERNING LAW STATE], without regard
     to its conflict of laws principles. The parties consent to venue in [VENUE].

12. GENERAL

12.1 The Developer is an independent contractor and not an employee, partner or agent of the
     Brand. This Agreement is the entire agreement between the parties on its subject matter and
     may be amended only in a writing signed by both parties. Neither party may assign it without
     the other's prior written consent, except that the Brand may assign to a successor in
     connection with a merger, financing or sale of substantially all assets. A breach of
     Section 7 or Section 8 may cause harm for which monetary damages alone may be inadequate, and
     the non-breaching party may seek equitable relief. If any provision is held unenforceable,
     the remainder continues in effect. This Agreement may be signed in counterparts, including by
     electronic signature, each of which is an original.

IN WITNESS WHEREOF, the parties have executed this Agreement as of the Effective Date.


[COMPANY NAME]                            [DEVELOPER NAME]


By: _______________________________       By: _______________________________

Name: _____________________________       Name: _____________________________

Title: ____________________________       Title: ____________________________

Date: _____________________________       Date: _____________________________
`,
  },

  {
    id: "product-development-agreement",
    docId: "product-development-agreement",
    title: "PRODUCT DEVELOPMENT SERVICES AGREEMENT",
    version: "1.0",
    lastReviewed: "2026-08-25",

    founderGuide: {
      useThisWhen:
        "A development engagement is big enough to have phases — concept work, formulation, packaging development, pilot runs, handover to manufacturing — and more than one person will be involved on the other side. Use this as the umbrella and put the actual scope in a statement of work.",
      whyItMatters:
        "This is the document that decides whether a stalled project is recoverable. Development programmes stop for ordinary reasons: funding runs short, the co-packer changes, a sensory target proves unreachable. What matters then is whether you hold the work done to date, whether you can take it to someone else, and whether you owe for phases never delivered. A programme papered as a proposal and an invoice leaves all three open, and the answer defaults to whoever holds the files.",
      negotiateCarefully: [
        {
          point: "Ownership of partial and rejected work",
          detail:
            "Most programmes that stop, stop mid-phase. Ownership that vests only on completion, or only on payment of the full programme fee, means a paused project leaves your work sitting on someone else's server.",
        },
        {
          point: "The handover package, listed item by item",
          detail:
            "'Reasonable assistance' is not a handover. Name the artefacts: formula and specification, process parameters, analytical methods, trial data, stability results, artwork files and packaging specifications, supplier contacts. Make it deliverable on termination, not only on completion.",
        },
        {
          point: "Acceptance windows and deemed acceptance",
          detail:
            "A short deemed-acceptance window converts a deliverable you have not tested into one you have approved and must pay for. Size the window to include an actual trial, and make the clock restart on redelivery.",
        },
        {
          point: "Change control and what it costs",
          detail:
            "Development scope moves constantly, and a pack format or plant change mid-programme is effectively a new project. Agree the change process and its pricing mechanics before the first change request arrives, not in the middle of one.",
        },
        {
          point: "What sits outside the fee",
          detail:
            "Pilot materials, plant time, third-party laboratory work, stability studies and label review are real money and are usually excluded unless named. Write down which side pays for each before the programme starts.",
        },
      ],
      redFlags: [
        "Ownership contingent on completion of the whole programme or on payment of disputed amounts.",
        "Deemed acceptance in a short window with no trial.",
        "No rework remedy where a deliverable fails at scale.",
        "No termination right, or termination with the full remaining fee payable.",
        "A handover obligation described only as 'reasonable assistance'.",
        "No statement of who is responsible for label, claims and regulatory review.",
      ],
      timeToComplete: "45 minutes to fill, plus the statement of work · 2 to 3 weeks to agree",
    },

    placeholders: [
      { token: "[EFFECTIVE DATE]", label: "Effective date", example: "September 1, 2026" },
      { token: "[COMPANY NAME]", label: "Your legal entity name", brandKey: "legalEntityName", example: "Acme Beverage Co., LLC" },
      { token: "[COMPANY STATE]", label: "Your state of formation", brandKey: "stateOfFormation", example: "Delaware" },
      { token: "[COMPANY ENTITY TYPE]", label: "Your entity type", example: "limited liability company" },
      { token: "[COMPANY ADDRESS]", label: "Your address", brandKey: "principalAddress", example: "123 Main St, Austin, TX 78701" },
      { token: "[DEVELOPER NAME]", label: "Development partner's legal name", example: "Northfield Product Development, Inc." },
      { token: "[DEVELOPER STATE]", label: "Development partner's state of formation", example: "Minnesota" },
      { token: "[DEVELOPER ENTITY TYPE]", label: "Development partner's entity type", example: "corporation" },
      { token: "[DEVELOPER ADDRESS]", label: "Development partner's address", example: "410 Mill Rd, Minneapolis, MN 55401" },
      { token: "[BRAND NAME]", label: "Your brand name", brandKey: "tradeName", example: "Acme" },
      { token: "[PROGRAMME DESCRIPTION]", label: "What the programme covers", example: "development of a three-SKU sparkling line through to first commercial run" },
      { token: "[PRODUCT CATEGORY]", label: "Product category", brandKey: "productCategory", example: "functional beverage" },
      { token: "[SOW REFERENCE]", label: "Statement of work reference", example: "SOW-1, dated September 1, 2026" },
      { token: "[PROGRAMME PERIOD]", label: "Target programme duration", example: "9 months" },
      { token: "[ACCEPTANCE PERIOD]", label: "Days to evaluate a deliverable", example: "20 business days" },
      { token: "[TESTING RESPONSIBILITY]", label: "Who arranges and pays for third-party testing", example: "the Brand, on the Developer's written recommendation" },
      { token: "[REGULATORY RESPONSIBILITY]", label: "Who is responsible for label and claims review", example: "the Brand, using its own advisers" },
      { token: "[PROGRAMME FEE]", label: "Total programme fee", example: "120,000 across five phases" },
      { token: "[PAYMENT DAYS]", label: "Payment terms in days", example: "30 days" },
      { token: "[CHANGE RESPONSE DAYS]", label: "Days to respond to a change request", example: "10 business days" },
      { token: "[CURE PERIOD]", label: "Cure period for a material breach", example: "30 days" },
      { token: "[TERMINATION NOTICE]", label: "Notice to terminate for convenience", example: "30 days" },
      { token: "[HANDOVER DAYS]", label: "Days to deliver the handover package", example: "15 business days" },
      { token: "[NON-USE YEARS]", label: "Years the category use restriction runs", example: "3" },
      { token: "[LIABILITY CAP]", label: "Liability cap", example: "the fees paid under the affected statement of work" },
      { token: "[GOVERNING LAW STATE]", label: "Governing law state", example: "Delaware" },
      { token: "[VENUE]", label: "Venue for disputes", example: "Wilmington, Delaware" },
    ],

    body: `This Product Development Services Agreement (this "Agreement") is entered into as of
[EFFECTIVE DATE] (the "Effective Date") by and between:

  [COMPANY NAME], a [COMPANY STATE] [COMPANY ENTITY TYPE], with a principal place of business
  at [COMPANY ADDRESS] (the "Brand"); and

  [DEVELOPER NAME], a [DEVELOPER STATE] [DEVELOPER ENTITY TYPE], with a principal place of
  business at [DEVELOPER ADDRESS] (the "Developer").

1. STRUCTURE OF THIS AGREEMENT

1.1 This Agreement sets the standing terms for development services. The work itself is described
    in one or more statements of work, the first of which is [SOW REFERENCE] (each, an "SOW").

1.2 Each SOW must state: the phases and their deliverables; the acceptance criteria for each
    deliverable; the fees and payment triggers; the timeline and any dependency on the Brand or a
    third party; and the handover package for that SOW.

1.3 If an SOW conflicts with this Agreement, this Agreement controls, except where the SOW says
    expressly that it amends a numbered section of this Agreement for that SOW only.

2. THE PROGRAMME

2.1 The Developer shall perform the development programme described as [PROGRAMME DESCRIPTION]
    for products to be sold under the [BRAND NAME] brand in the [PRODUCT CATEGORY] category (the
    "Programme").

2.2 The parties target completion of the Programme within [PROGRAMME PERIOD]. Each party shall
    notify the other promptly when it becomes aware that a milestone is at risk, identifying the
    cause and the party or third party the delay depends on.

2.3 The Developer shall assign a named programme lead and shall not replace that person without
    notifying the Brand.

3. DELIVERABLES AND ACCEPTANCE

3.1 The Developer shall deliver each deliverable described in the applicable SOW, complete in the
    form the SOW specifies. A deliverable missing a specified element is incomplete and does not
    start the acceptance period.

3.2 The Brand has [ACCEPTANCE PERIOD] after delivery to evaluate a deliverable against its
    acceptance criteria and to notify the Developer in writing of acceptance or of the respects in
    which the deliverable does not conform.

3.3 Where a deliverable can only be properly evaluated by a production trial, the acceptance
    period begins when the trial is completed, and the parties shall schedule the trial in good
    faith.

3.4 If the Brand notifies non-conformance, the Developer shall correct and redeliver, and the
    acceptance period runs again from redelivery. If a deliverable fails acceptance after the
    number of correction cycles stated in the SOW, the Brand may reject it, terminate the affected
    phase, and recover the fees paid for that phase.

3.5 Acceptance does not waive a warranty or a remedy for a defect not reasonably discoverable
    during the acceptance period.

4. CHANGE REQUESTS

4.1 Either party may request a change to an SOW in writing. The request must describe the change
    and the reason for it.

4.2 The Developer shall respond within [CHANGE RESPONSE DAYS] with the effect of the change on
    scope, fees, timeline and deliverables.

4.3 No change is effective, and no additional fee is payable, until both parties sign a written
    change order. The Developer shall continue performing the unchanged scope while a change
    request is open.

4.4 A change of pack format, fill size, filling line or nominated manufacturer is treated as a
    change request and priced accordingly.

5. TESTING RESPONSIBILITIES

5.1 The Developer is responsible for bench and laboratory work within the SOW scope, for
    documenting methods and results, and for identifying testing it recommends before a
    deliverable is accepted.

5.2 Third-party testing — including stability, shelf-life, microbiological, nutritional analysis
    and sensory panel work — is arranged and paid for by [TESTING RESPONSIBILITY], unless the SOW
    says otherwise for a specific test.

5.3 Pilot and trial materials, plant time and freight for trial material are outside the fee and
    are payable by the Brand only where approved by the Brand in writing in advance.

5.4 Each party shall share test results relating to the Programme with the other promptly,
    including results that are unfavourable.

6. REGULATORY AND LABEL RESPONSIBILITIES

6.1 Responsibility for label copy, ingredient statements, nutritional panels, claims substantiation
    and any regulatory review of the finished product rests with [REGULATORY RESPONSIBILITY].

6.2 The Developer is responsible for providing accurate ingredient identities, usage rates,
    allergen information, supplier documentation in its possession, and the technical inputs the
    responsible party needs in order to carry out the review described in Section 6.1.

6.3 The Brand is responsible for deciding which markets and channels the product is sold into and
    for the marketing claims it makes. The Developer is not the Brand's adviser on those matters
    and the Brand is not entitled to rely on it as such.

6.4 Each party shall promptly notify the other of any information suggesting that an ingredient,
    process or claim contemplated by the Programme is unsuitable for the intended use.

7. FEES AND PAYMENT

7.1 The Programme fee is [PROGRAMME FEE], allocated across phases as set out in the applicable
    SOW, with each phase payable on the trigger stated in that SOW.

7.2 Invoices are payable within [PAYMENT DAYS] of receipt. Amounts genuinely disputed in good
    faith may be withheld while the dispute is resolved, provided the Brand pays the undisputed
    balance and notifies the basis of the dispute.

7.3 Expenses are reimbursable only where approved in writing in advance.

8. OWNERSHIP OF WORK PRODUCT

8.1 "Work Product" means everything created or developed by the Developer for the Brand under any
    SOW, including formulas, specifications, process parameters, analytical methods, prototypes,
    packaging development files, artwork files, test data, reports, and all partial, superseded
    and rejected versions of any of them, together with every modification and improvement of them
    however arising.

8.2 As between the parties, the Brand exclusively owns the Work Product and all intellectual
    property rights in it. The Developer hereby irrevocably assigns to the Brand all right, title
    and interest it has or may acquire in the Work Product, effective on creation. Where a right
    cannot be assigned, the Developer grants the Brand an exclusive, perpetual, irrevocable,
    worldwide, fully paid, sublicensable licence to use, modify, disclose and have products made
    under it.

8.3 Ownership vests on creation and is not conditional on completion of any phase or on payment
    of any disputed amount. The Developer shall not assert a lien or retention right over any Work
    Product.

8.4 The Developer retains ownership of its pre-existing tools, methods, templates and general
    know-how ("Background IP"). Where Background IP is embedded in a deliverable, the Developer
    grants the Brand a perpetual, irrevocable, worldwide, fully paid, sublicensable licence to use
    it as part of the Work Product. The Developer shall identify any Background IP it embeds.

8.5 The Brand may disclose the Work Product to any manufacturer, supplier, laboratory, investor or
    acquirer under confidentiality obligations of the Brand's choosing.

9. HANDOVER

9.1 On completion of an SOW, on termination for any reason, or on the Brand's written request, the
    Developer shall deliver the handover package within [HANDOVER DAYS].

9.2 Unless the SOW lists a different package, the handover package comprises: the current formula
    and specification; process parameters and equipment assumptions; analytical methods and test
    protocols; all test, trial and stability data; packaging specifications and artwork files in
    editable format; supplier and component identities with part numbers; open issues and known
    risks; and all partial and rejected work.

9.3 The Developer shall provide reasonable technical explanation of the handover package to the
    Brand or to a party the Brand designates, at the Developer's then-current rates for technical
    services where the handover follows completion, and at no charge where the handover follows
    the Developer's material breach.

10. CONFIDENTIALITY AND NON-USE

10.1 Each party shall hold the other's non-public information in confidence, use it only for the
     Programme, and disclose it only to personnel and advisers who need it and who are bound by
     obligations at least as protective as this Section 10.

10.2 During the Programme and for [NON-USE YEARS] years afterwards, the Developer shall not use
     the Work Product, or any development approach derived from it, to develop or produce a
     competing [PRODUCT CATEGORY] product for itself or any third party.

10.3 Obligations with respect to information that constitutes a trade secret continue for as long
     as it remains a trade secret.

11. WARRANTIES AND REMEDIES

11.1 The Developer represents and warrants that it will perform with the skill and care ordinarily
     expected of a professional development firm, that it has the right to make the assignment in
     Section 8, that the Work Product is original to it other than identified third-party
     components, and that its personnel are free of conflicting obligations.

11.2 Where a deliverable fails to meet its acceptance criteria, the Developer shall rework it at
     its own cost. Where the failure appears only at scale and is attributable to the Developer's
     work, the rework obligation applies equally.

11.3 Except for breaches of Section 8 or Section 10, and for a party's gross negligence or wilful
     misconduct, each party's aggregate liability under this Agreement is limited to
     [LIABILITY CAP].

12. TERM AND TERMINATION

12.1 This Agreement begins on the Effective Date and continues until terminated. It continues to
     govern every SOW in effect when notice of termination is given, unless the parties agree
     otherwise.

12.2 Either party may terminate this Agreement or any SOW if the other materially breaches and
     does not cure within [CURE PERIOD] after written notice.

12.3 The Brand may terminate any SOW for convenience on [TERMINATION NOTICE] written notice. On
     such termination the Brand pays for work properly performed to the effective date of
     termination and for non-cancellable commitments approved in advance, and nothing further.

12.4 Sections 8, 9, 10, 11, 13 and 14 survive termination or expiry.

13. GOVERNING LAW AND VENUE

13.1 This Agreement is governed by the laws of the State of [GOVERNING LAW STATE], without regard
     to its conflict of laws principles. The parties consent to venue in [VENUE].

14. GENERAL

14.1 The Developer is an independent contractor. This Agreement, together with each SOW and change
     order, is the entire agreement between the parties on its subject matter and may be amended
     only in a writing signed by both parties. Neither party may assign it without the other's
     prior written consent, except that the Brand may assign to a successor in connection with a
     merger, financing or sale of substantially all assets. A breach of Section 8 or Section 10 may
     cause harm for which monetary damages alone may be inadequate, and the non-breaching party
     may seek equitable relief. Neither party may use the other's name or marks publicly without
     prior written consent. If any provision is held unenforceable, the remainder continues in
     effect. This Agreement may be signed in counterparts, including by electronic signature, each
     of which is an original.

IN WITNESS WHEREOF, the parties have executed this Agreement as of the Effective Date.


[COMPANY NAME]                            [DEVELOPER NAME]


By: _______________________________       By: _______________________________

Name: _____________________________       Name: _____________________________

Title: ____________________________       Title: ____________________________

Date: _____________________________       Date: _____________________________


─────────────────────────────────────────────────────────────────────────────
STATEMENT OF WORK [SOW REFERENCE]
─────────────────────────────────────────────────────────────────────────────

1. Phases and deliverables ................ __________________________________
2. Acceptance criteria per deliverable .... __________________________________
3. Correction cycles per deliverable ...... __________________________________
4. Fees and payment triggers per phase .... __________________________________
5. Timeline, dependencies and owners ...... __________________________________
6. Excluded scope and excluded costs ...... __________________________________
7. Handover package for this SOW .......... __________________________________
8. Named programme lead ................... __________________________________
`,
  },

  {
    id: "formula-assignment-agreement",
    docId: "formula-assignment-agreement",
    title: "FORMULA ASSIGNMENT AGREEMENT",
    version: "1.0",
    lastReviewed: "2026-08-25",

    founderGuide: {
      useThisWhen:
        "The formula exists but the paperwork does not. Work was done before the company was formed, by a friend, by a consultant paid on an invoice, or by a plant technologist as a favour, and nothing was ever assigned. This is the short document that closes the gap.",
      whyItMatters:
        "Almost every brand needs one of these eventually. Everything works commercially until an investor or a buyer asks for the document that makes the formula the company's, and there isn't one. Cleaning it up later means going back to people whose leverage has changed and whose interest in helping has usually declined. Sign it while the relationship is still warm, and while the person still remembers what they did.",
      negotiateCarefully: [
        {
          point: "Identify the version, not just 'the formula'",
          detail:
            "The version that matters is the one currently in production, which is often several modifications away from the original bench work. Name it in the schedule by version and date, and expressly include every modification made since.",
        },
        {
          point: "The ownership warranty",
          detail:
            "Someone who developed the formula while employed elsewhere may not hold the rights they are handing you. Ask for a warranty that they own what they are transferring and that no employer or other party has a claim.",
        },
        {
          point: "Consideration, actually given",
          detail:
            "An assignment signed years after the work with nothing in exchange is the version most easily argued about later. Give real consideration, recite it accurately, and keep the record of payment.",
        },
        {
          point: "Delivery of the records behind the formula",
          detail:
            "Owning the formula without the bench notes, trial data and analytical methods leaves you owning a document rather than the knowledge. Ask for the records, the data and any retained samples as part of the deal.",
        },
        {
          point: "Third-party components, stated honestly",
          detail:
            "A formula containing a supplier-owned flavour system or premix cannot be fully assigned. Carve it out in the schedule and pair the carve-out with a supply or specification arrangement rather than signing paper that overstates what moved.",
        },
      ],
      redFlags: [
        "An assignment with no ownership warranty from the assignor.",
        "A formula described generally rather than by version and date.",
        "No delivery obligation for records, data or retained samples.",
        "Silence on modifications made since the original development work.",
        "An assignor who declines to confirm they are free of employer obligations.",
      ],
      timeToComplete: "15 minutes to fill · same-day to sign",
    },

    placeholders: [
      { token: "[EFFECTIVE DATE]", label: "Effective date", example: "September 1, 2026" },
      { token: "[COMPANY NAME]", label: "Your legal entity name", brandKey: "legalEntityName", example: "Acme Beverage Co., LLC" },
      { token: "[COMPANY STATE]", label: "Your state of formation", brandKey: "stateOfFormation", example: "Delaware" },
      { token: "[COMPANY ENTITY TYPE]", label: "Your entity type", example: "limited liability company" },
      { token: "[COMPANY ADDRESS]", label: "Your address", brandKey: "principalAddress", example: "123 Main St, Austin, TX 78701" },
      { token: "[ASSIGNOR NAME]", label: "Assignor's full legal name", example: "Dana Reyes" },
      { token: "[ASSIGNOR DESCRIPTION]", label: "Assignor's status", example: "an individual" },
      { token: "[ASSIGNOR ADDRESS]", label: "Assignor's address", example: "77 Birch Ln, Portland, OR 97201" },
      { token: "[PRODUCT NAME]", label: "Product the formula makes", example: "Acme Sparkling Yuzu, 12 oz can" },
      { token: "[BRAND NAME]", label: "Your brand name", brandKey: "tradeName", example: "Acme" },
      { token: "[DEVELOPMENT PERIOD]", label: "When the work was done", example: "between March 2024 and January 2025" },
      { token: "[CONSIDERATION]", label: "Consideration for the assignment", example: "2,500, receipt acknowledged" },
      { token: "[DELIVERY DAYS]", label: "Days to deliver the records", example: "15 business days" },
      { token: "[EXCLUDED COMPONENTS]", label: "Third-party proprietary components, or 'none'", example: "Flavor system code 4471 supplied by Northline Flavors" },
      { token: "[GOVERNING LAW STATE]", label: "Governing law state", example: "Delaware" },
      { token: "[VENUE]", label: "Venue for disputes", example: "Wilmington, Delaware" },
    ],

    body: `This Formula Assignment Agreement (this "Agreement") is entered into as of [EFFECTIVE DATE]
(the "Effective Date") by and between:

  [ASSIGNOR NAME], [ASSIGNOR DESCRIPTION], of [ASSIGNOR ADDRESS] (the "Assignor"); and

  [COMPANY NAME], a [COMPANY STATE] [COMPANY ENTITY TYPE], with a principal place of business
  at [COMPANY ADDRESS] (the "Company").

RECITALS

A. [DEVELOPMENT PERIOD], the Assignor developed or contributed to the development of the
   formulation for [PRODUCT NAME], sold or to be sold under the [BRAND NAME] brand.

B. The parties did not previously record in writing who owns that formulation, and wish to do so
   now.

C. The Assignor is willing to assign the formulation and everything associated with it to the
   Company on the terms below, in exchange for the consideration described in Section 4.

1. DEFINITIONS

1.1 "Formula" means the formulation identified in Schedule 1, together with every version and
    every modification, adjustment, substitution, improvement and derivative of it made at any
    time up to the Effective Date, whether made by the Assignor or by any other person.

1.2 "Formula Records" means everything associated with the Formula that is in the Assignor's
    possession or control, including: the specification and process parameters; bench notes and
    formulation worksheets; trial and pilot records; analytical methods, test protocols and
    results; stability and shelf-life data; supplier identities, grades and part numbers;
    ingredient costings; correspondence about the formulation; and any retained samples.

1.3 "Excluded Components" means the third-party proprietary components identified as
    [EXCLUDED COMPONENTS], which are owned by their respective suppliers. If no component is
    identified, there are no Excluded Components.

2. ASSIGNMENT

2.1 The Assignor hereby irrevocably assigns and transfers to the Company all right, title and
    interest in and to the Formula and the Formula Records, together with all intellectual
    property rights in them, including trade secret rights and all rights of action for past
    interference with them.

2.2 The assignment in Section 2.1 is a present assignment effective on the Effective Date, is
    worldwide and perpetual, and is not conditional on any further act.

2.3 Where any right cannot be assigned, the Assignor grants the Company an exclusive, perpetual,
    irrevocable, worldwide, fully paid licence to use, reproduce, modify, disclose and have
    products made under that right, with the right to sublicense.

2.4 The Assignor retains no right to use the Formula or the Formula Records for any purpose, for
    the Assignor's own account or for any third party.

3. DELIVERY

3.1 Within [DELIVERY DAYS] of the Effective Date, the Assignor shall deliver the Formula and the
    Formula Records to the Company in the most complete form available, and shall then destroy or
    return any remaining copies.

3.2 The Assignor shall, on the Company's reasonable request, explain any part of the Formula or
    the Formula Records that the Company or a manufacturer it designates does not understand.

4. CONSIDERATION

4.1 In consideration of the assignment, the Company shall pay the Assignor [CONSIDERATION]. The
    Assignor acknowledges that this consideration is adequate and is given in exchange for the
    assignment made in Section 2.

5. THIRD-PARTY COMPONENTS

5.1 The Excluded Components are owned by their respective suppliers and are not assigned by this
    Agreement.

5.2 For each Excluded Component, the Assignor shall provide the supplier name, the supplier's code
    or part number, the function, the usage rate, and any allergen, certification and
    characterisation information the Assignor holds.

5.3 The Assignor represents that, other than the Excluded Components, no part of the Formula is
    owned by or licensed from a third party.

6. ASSIGNOR WARRANTIES

6.1 The Assignor represents and warrants that:
    (a) the Assignor owns the Formula and the Formula Records and has full right and authority to
        assign them;
    (b) the Assignor has not assigned, licensed, pledged or otherwise transferred any interest in
        them to any other person;
    (c) no employer, client or other party of the Assignor has or may claim any right in them,
        including by reason of work performed during any employment or engagement;
    (d) the Assignor is not subject to any agreement that conflicts with this Agreement; and
    (e) to the Assignor's knowledge, the Formula does not infringe the rights of any third party.

7. CONFIDENTIALITY AND NON-USE

7.1 The Assignor shall keep the Formula and the Formula Records confidential, shall not disclose
    them to any person, and shall not use them for any purpose. These obligations continue
    indefinitely and are not limited by the term of any other agreement between the parties.

8. FURTHER ASSURANCES

8.1 The Assignor shall, at the Company's request and expense, sign any further document and take
    any further action the Company reasonably requires to confirm, record, perfect or enforce the
    assignment, including in connection with a financing, a sale of the business or a dispute with
    a third party. This obligation continues after the Effective Date without time limit.

9. GOVERNING LAW AND VENUE

9.1 This Agreement is governed by the laws of the State of [GOVERNING LAW STATE], without regard
    to its conflict of laws principles. The parties consent to venue in [VENUE].

10. GENERAL

10.1 This Agreement is the entire agreement between the parties on its subject matter and may be
     amended only in a writing signed by both parties. The Company may assign this Agreement,
     including to a successor in connection with a merger, financing or sale of substantially all
     assets. The Assignor acknowledges that a breach of Section 2, 3 or 7 may cause harm for which
     monetary damages alone may be inadequate, and that the Company may seek equitable relief. If
     any provision is held unenforceable, the remainder continues in effect. This Agreement may be
     signed in counterparts, including by electronic signature, each of which is an original.

IN WITNESS WHEREOF, the parties have executed this Agreement as of the Effective Date.


[ASSIGNOR NAME]                           [COMPANY NAME]


By: _______________________________       By: _______________________________

Name: _____________________________       Name: _____________________________

Title: ____________________________       Title: ____________________________

Date: _____________________________       Date: _____________________________


─────────────────────────────────────────────────────────────────────────────
SCHEDULE 1 — THE FORMULA
─────────────────────────────────────────────────────────────────────────────

Product ................................... [PRODUCT NAME]
Formula name or internal number ........... __________________________________
Version assigned .......................... __________________________________
Date of that version ...................... __________________________________
Earlier versions included ................. __________________________________
Modifications made since development ...... __________________________________
Where the current version is held ......... __________________________________
Records delivered ......................... __________________________________
Retained samples delivered ................ __________________________________
Excluded Components ....................... [EXCLUDED COMPONENTS]
`,
  },

  {
    id: "flavor-house-agreement",
    docId: "flavor-house-agreement",
    title: "FLAVOR DEVELOPMENT AND SUPPLY AGREEMENT",
    version: "1.0",
    lastReviewed: "2026-08-25",

    founderGuide: {
      useThisWhen:
        "A flavour house is developing a custom flavour system for you and will then sell it to you, or to your co-manufacturer, as an ingredient. Sign it while development is still ahead of you — that is the only moment the supply terms are genuinely negotiable.",
      whyItMatters:
        "Flavour houses commonly keep ownership of the flavour system and supply it as an ingredient. That is the normal model, not a trap, and it is why custom development is often done at little or no upfront cost. But it means your formula contains a layer you do not own and cannot move: your specification names a code, and one company makes it. If they discontinue it, reprice it, impose a minimum you cannot use before it expires, or decide your volume is not worth serving, your product stops. This agreement is not an attempt to win ownership. It is an attempt to make each of those outcomes survivable.",
      negotiateCarefully: [
        {
          point: "A continuing supply commitment, in writing",
          detail:
            "Ask for an obligation to keep supplying at agreed terms for a defined period, rather than a quote that is revisited each order. Without it you have a purchase history, not a supply arrangement, and the ingredient can simply stop being offered.",
        },
        {
          point: "Discontinuation notice and last-time-buy",
          detail:
            "The realistic failure mode is not bad faith, it is a low-volume code being rationalised. A stated notice period plus the right to place a final order sized to your qualification timeline is what turns that from a dead SKU into a reformulation project.",
        },
        {
          point: "Transfer or escrow of the specification on defined triggers",
          detail:
            "Ask for the specification to be released to you or to a nominated second house if the flavour is discontinued, if supply fails for a stated period, or on insolvency. Escrow with a third party is the version a flavour house is most likely to accept.",
        },
        {
          point: "Minimums measured against your real forecast and the flavour's shelf life",
          detail:
            "Flavour minimums are sized for larger customers, and a compounded flavour has its own expiry. A minimum you cannot consume before it expires is a write-off dressed as a discount. Test both the order minimum and any annual minimum against a forecast you actually believe.",
        },
        {
          point: "What free development is being paid for",
          detail:
            "Development at no charge is usually paid for with exclusivity, an annual volume commitment, or a period in which you cannot second-source. Get the exchange written down, and size the commitment to your forecast rather than to your ambition.",
        },
      ],
      redFlags: [
        "An annual minimum purchase set well above your realistic forecast.",
        "No notice period before discontinuation, and no last-time-buy right.",
        "No restriction at all on selling the identical system into your category.",
        "Unlimited price-increase rights on an ingredient with a single source.",
        "The flavour supplied only through your co-manufacturer, with no direct contract between you and the house.",
        "Refusal to discuss any escrow, transfer or second-source arrangement.",
      ],
      timeToComplete: "40 minutes to fill · 2 to 4 weeks to negotiate supply terms",
    },

    placeholders: [
      { token: "[EFFECTIVE DATE]", label: "Effective date", example: "September 1, 2026" },
      { token: "[COMPANY NAME]", label: "Your legal entity name", brandKey: "legalEntityName", example: "Acme Beverage Co., LLC" },
      { token: "[COMPANY STATE]", label: "Your state of formation", brandKey: "stateOfFormation", example: "Delaware" },
      { token: "[COMPANY ENTITY TYPE]", label: "Your entity type", example: "limited liability company" },
      { token: "[COMPANY ADDRESS]", label: "Your address", brandKey: "principalAddress", example: "123 Main St, Austin, TX 78701" },
      { token: "[FLAVOR HOUSE NAME]", label: "Flavour house legal name", example: "Northline Flavors, Inc." },
      { token: "[FLAVOR HOUSE STATE]", label: "Flavour house state of formation", example: "New Jersey" },
      { token: "[FLAVOR HOUSE ENTITY TYPE]", label: "Flavour house entity type", example: "corporation" },
      { token: "[FLAVOR HOUSE ADDRESS]", label: "Flavour house address", example: "2 Harbor Rd, Newark, NJ 07102" },
      { token: "[BRAND NAME]", label: "Your brand name", brandKey: "tradeName", example: "Acme" },
      { token: "[PRODUCT NAME]", label: "Product the flavour is for", example: "Acme Sparkling Yuzu, 12 oz can" },
      { token: "[PRODUCT CATEGORY]", label: "Product category", brandKey: "productCategory", example: "functional beverage" },
      { token: "[FLAVOR NAME]", label: "Name of the flavour system", example: "Yuzu Bright WONF" },
      { token: "[FLAVOR CODE]", label: "Flavour house code or part number", example: "4471" },
      { token: "[TARGET PROFILE]", label: "Target sensory profile brief", example: "bright citrus top note, low bitterness, clean finish at pH 3.4" },
      { token: "[DEVELOPMENT FEE]", label: "Development fee, or what is given instead", example: "no fee, in exchange for the commitment in Section 3.4" },
      { token: "[DEVELOPMENT ROUNDS]", label: "Development rounds included", example: "3" },
      { token: "[SAMPLE QUANTITY]", label: "Sample quantity per round", example: "500 g" },
      { token: "[FLAVOR PRICE]", label: "Price of the flavour system", example: "62.00" },
      { token: "[PRICE UNIT]", label: "Unit the price is quoted per", example: "kilogram" },
      { token: "[PRICE VALIDITY PERIOD]", label: "How long a price holds", example: "12 months from the Effective Date" },
      { token: "[PRICE CHANGE NOTICE]", label: "Notice before a price change", example: "90 days" },
      { token: "[PRICE CHANGE CAP]", label: "Limit on any single increase", example: "5 percent in any 12-month period" },
      { token: "[MINIMUM ORDER QUANTITY]", label: "Minimum order quantity", example: "25 kg" },
      { token: "[ANNUAL MINIMUM]", label: "Annual minimum purchase, or 'none'", example: "none" },
      { token: "[LEAD TIME]", label: "Lead time from order to delivery", example: "6 weeks" },
      { token: "[SAFETY STOCK]", label: "Safety stock held for you", example: "one minimum order quantity" },
      { token: "[FORECAST HORIZON]", label: "Rolling forecast horizon", example: "6 months" },
      { token: "[BINDING FORECAST WINDOW]", label: "Portion of the forecast that is binding", example: "the first 6 weeks" },
      { token: "[SUPPLY TERM]", label: "Period of the supply commitment", example: "3 years, renewing annually" },
      { token: "[DISCONTINUATION NOTICE]", label: "Notice before discontinuation", example: "12 months" },
      { token: "[LAST-TIME-BUY WINDOW]", label: "Window to place a final order", example: "6 months from the discontinuation notice" },
      { token: "[SUPPLY FAILURE PERIOD]", label: "Failure period that triggers second-source rights", example: "45 days beyond the agreed lead time" },
      { token: "[ESCROW AGENT]", label: "Escrow agent, or 'to be agreed'", example: "a mutually agreed escrow agent" },
      { token: "[RESTRICTED COMPETITOR]", label: "Named competitor the flavour may not be sold to", example: "Bright Point Beverages, Inc." },
      { token: "[RESTRICTION PERIOD]", label: "How long that restriction runs", example: "24 months from first commercial shipment" },
      { token: "[MANUFACTURER NAME]", label: "Your co-manufacturer, or 'none'", example: "Midwest Co-Packing, Inc." },
      { token: "[PAYMENT DAYS]", label: "Payment terms in days", example: "30 days" },
      { token: "[DELIVERY TERMS]", label: "Delivery terms and destination", example: "delivered to the Manufacturer's Columbus, OH plant" },
      { token: "[GOVERNING LAW STATE]", label: "Governing law state", example: "Delaware" },
      { token: "[VENUE]", label: "Venue for disputes", example: "Wilmington, Delaware" },
    ],

    body: `This Flavor Development and Supply Agreement (this "Agreement") is entered into as of
[EFFECTIVE DATE] (the "Effective Date") by and between:

  [COMPANY NAME], a [COMPANY STATE] [COMPANY ENTITY TYPE], with a principal place of business
  at [COMPANY ADDRESS] (the "Brand"); and

  [FLAVOR HOUSE NAME], a [FLAVOR HOUSE STATE] [FLAVOR HOUSE ENTITY TYPE], with a principal place
  of business at [FLAVOR HOUSE ADDRESS] (the "Flavor House").

RECITALS

A. The Brand sells or intends to sell [PRODUCT NAME] under the [BRAND NAME] brand in the
   [PRODUCT CATEGORY] category.

B. The Flavor House will develop a custom flavor system for that product and will supply it to
   the Brand, or to a manufacturer the Brand designates, as an ingredient.

C. The parties acknowledge the commercial reality this Agreement is built on: the Flavor House
   retains ownership of the flavor system it develops, and the Brand's protection lies in the
   supply, continuity and transfer terms set out below rather than in ownership.

1. DEFINITIONS

1.1 "Flavor System" means the custom flavor developed under this Agreement, identified as
    [FLAVOR NAME], code [FLAVOR CODE], including its composition, its manufacturing process and
    the know-how used to produce it.

1.2 "Flavor Specification" means the document describing the Flavor System as supplied, including
    identity, physical and analytical parameters, recommended usage rate, allergen status,
    certification status, characterisation for ingredient-statement purposes, storage conditions
    and shelf life.

1.3 "Brand Materials" means the Brand's brief, target profile, product concept, formulation,
    volumes, pricing, customer information and all other non-public information of the Brand,
    including [TARGET PROFILE].

1.4 "Manufacturer" means [MANUFACTURER NAME] or any other manufacturer the Brand designates in
    writing.

2. DEVELOPMENT

2.1 The Flavor House shall develop a Flavor System to the target profile described as
    [TARGET PROFILE], for use in [PRODUCT NAME].

2.2 Development includes [DEVELOPMENT ROUNDS] rounds. Each round consists of a submission of
    [SAMPLE QUANTITY] of flavor sample, a written note of what changed, and a review with the
    Brand.

2.3 The development fee is [DEVELOPMENT FEE]. Where development is provided at no fee or at a
    reduced fee, what the Brand gives in exchange is stated expressly in this Agreement and
    nowhere else.

2.4 The Flavor System is accepted when the Brand confirms in writing that it performs to the
    target profile in the finished product, tested in the Brand's product rather than in a model
    system.

3. OWNERSHIP

3.1 The Flavor House owns the Flavor System, including its composition and its manufacturing
    know-how. Nothing in this Agreement transfers ownership of the Flavor System to the Brand.

3.2 The Brand owns the Brand Materials, the finished-product formulation and the specification for
    the finished product. The Flavor House acquires no interest in any of them, and the fact that
    the Flavor System is a component of the Brand's formulation gives the Flavor House no interest
    in that formulation.

3.3 The Flavor House shall not use the Brand Materials for any purpose other than performing this
    Agreement, and shall not use the Brand's brief or target profile to develop a flavor for any
    other customer.

3.4 In exchange for the development terms in Section 2.3, the Brand commits to the purchase
    arrangements in Sections 4 and 5. Neither party has any other obligation in respect of
    development, and no exclusivity is granted except as stated expressly in Section 9.

4. SUPPLY COMMITMENT

4.1 During [SUPPLY TERM] (the "Supply Term"), the Flavor House shall supply the Flavor System to
    the Brand and to the Manufacturer, on the terms of this Agreement, in the quantities ordered
    up to the volumes covered by the Brand's forecast.

4.2 The supply commitment is non-exclusive on the Brand's side: the Brand is not obliged to
    purchase the Flavor System exclusively from the Flavor House where a second source is
    permitted under Section 8, and is not obliged to purchase any quantity except any annual
    minimum stated in Section 5.3.

4.3 The Flavor House shall maintain [SAFETY STOCK] of the Flavor System, or of the components
    needed to produce it, available for the Brand.

4.4 The Flavor House shall not suspend supply on account of a commercial dispute while undisputed
    invoices are current.

5. ORDERS, MINIMUMS AND FORECASTING

5.1 The minimum order quantity is [MINIMUM ORDER QUANTITY].

5.2 The lead time from a confirmed order to delivery is [LEAD TIME]. The Flavor House shall notify
    the Brand promptly if a confirmed order will be late, and shall state the revised date.

5.3 Any annual minimum purchase obligation is [ANNUAL MINIMUM]. If no annual minimum is stated,
    the Brand has no annual purchase obligation.

5.4 The Brand shall provide a rolling forecast covering [FORECAST HORIZON], updated monthly.
    [BINDING FORECAST WINDOW] of each forecast is binding; the balance is a planning estimate and
    creates no purchase obligation.

5.5 Delivery is [DELIVERY TERMS]. Where the Flavor System is delivered to the Manufacturer, title
    and the commercial relationship remain with the Brand, and the Flavor House shall copy the
    Brand on all order confirmations, pricing and delivery notices sent to the Manufacturer.

6. PRICE

6.1 The price of the Flavor System is [FLAVOR PRICE] per [PRICE UNIT].

6.2 That price is firm for [PRICE VALIDITY PERIOD].

6.3 After the validity period, the Flavor House may change the price only on [PRICE CHANGE NOTICE]
    written notice, and any single increase is limited to [PRICE CHANGE CAP]. Any notice of
    increase must state the reason for it and identify the cost movement it responds to.

6.4 If the Brand does not accept a proposed increase, the Brand may place a final order at the
    existing price for delivery within the lead time, and may terminate the Supply Term on written
    notice without liability for any unfulfilled minimum.

6.5 Invoices are payable within [PAYMENT DAYS] of receipt.

7. DISCONTINUATION, LAST-TIME BUY AND TRANSFER

7.1 The Flavor House shall give the Brand at least [DISCONTINUATION NOTICE] written notice before
    discontinuing the Flavor System, ceasing to supply it to the Brand, or making any change to
    its composition, characterisation, allergen status or certification status.

7.2 Following a discontinuation notice, the Brand may place one or more final orders during
    [LAST-TIME-BUY WINDOW], at the price then in effect, in any quantity the Brand specifies, for
    delivery on the agreed lead time.

7.3 On any of the following triggers, the Flavor House shall promptly deliver the Flavor
    Specification and the manufacturing instructions for the Flavor System to the Brand, and the
    Brand may use them, and permit a third party to use them, solely to have the Flavor System
    produced for the Brand's own products:
    (a) discontinuation of the Flavor System, or cessation of supply to the Brand;
    (b) failure to supply conforming Flavor System for [SUPPLY FAILURE PERIOD];
    (c) the Flavor House ceasing business, or becoming subject to an insolvency or similar
        proceeding; or
    (d) a material breach of this Agreement by the Flavor House that is not cured after written
        notice.

7.4 As an alternative to Section 7.3, the parties may agree that the Flavor House deposits the
    Flavor Specification and manufacturing instructions with [ESCROW AGENT], on terms providing
    for release to the Brand on the triggers in Section 7.3. The Flavor House shall keep any
    deposit current with the version being supplied.

7.5 Material released under Section 7.3 or Section 7.4 remains confidential information of the
    Flavor House, may be used only for the purpose stated, and must be returned or destroyed when
    the Brand no longer requires it for that purpose.

8. SECOND SOURCE

8.1 If the Flavor House fails to supply conforming Flavor System for [SUPPLY FAILURE PERIOD], or
    on any other trigger in Section 7.3, the Brand may have the Flavor System, or a functional
    equivalent, produced by another supplier for the Brand's own products.

8.2 The Brand may at any time, without breaching this Agreement, develop or qualify an alternative
    flavor with another supplier, provided it does not disclose the Flavor Specification or the
    Flavor House's manufacturing instructions in doing so except as permitted by Section 7.

9. RESTRICTION ON SALE TO A NAMED COMPETITOR

9.1 For [RESTRICTION PERIOD], the Flavor House shall not sell or offer the Flavor System, or a
    flavor that is substantially identical to it in sensory profile, to [RESTRICTED COMPETITOR],
    whether under the same code or a different one.

9.2 The restriction in Section 9.1 does not prevent the Flavor House from selling its standard
    catalogue flavors, from using its own general know-how, or from developing flavors for other
    customers that are not substantially identical to the Flavor System.

9.3 The Flavor House shall not identify the Brand as a customer, or reference the Brand's product,
    in any sales material or presentation without the Brand's prior written consent.

10. SPECIFICATION, DOCUMENTATION AND QUALITY

10.1 The Flavor House shall provide and keep current the Flavor Specification, and shall provide a
     certificate of analysis with each lot showing the named parameters, the methods used and the
     actual values obtained.

10.2 The Flavor House shall provide and keep current the documentation supporting the allergen
     status, certification status and ingredient-statement characterisation of the Flavor System,
     and shall notify the Brand promptly if any of them changes or lapses.

10.3 The Flavor House shall notify the Brand in writing before any change to the composition,
     process, production site, sub-supplier or specification of the Flavor System, and shall not
     implement a change affecting the finished product without the Brand's written approval.

10.4 The Brand may reject any lot that does not conform to the Flavor Specification. The Flavor
     House shall replace rejected material at its cost and on an expedited basis, and the parties
     shall discuss in good faith the treatment of a production run affected by non-conforming
     material.

10.5 Each party shall cooperate promptly with the other in any product withdrawal, recall or
     traceability investigation involving the Flavor System, including providing lot-level
     traceability information on request.

11. CONFIDENTIALITY

11.1 Each party shall hold the other's non-public information in confidence, use it only for this
     Agreement, and disclose it only to personnel and advisers who need it and who are bound by
     obligations at least as protective as this Section 11.

11.2 The Brand may disclose the Flavor Specification and related documentation to the Manufacturer,
     to a laboratory, and to an investor or acquirer, in each case under confidentiality
     obligations at least as protective as this Section 11.

11.3 Obligations with respect to information that constitutes a trade secret continue for as long
     as it remains a trade secret.

12. TERM AND TERMINATION

12.1 This Agreement begins on the Effective Date and continues for the Supply Term unless
     terminated earlier.

12.2 Either party may terminate on written notice if the other materially breaches and does not
     cure within a reasonable period after notice.

12.3 On expiry or termination, the Brand may place a final order under Section 7.2, and Sections 3,
     7, 8, 9, 11, 13 and 14 survive.

13. GOVERNING LAW AND VENUE

13.1 This Agreement is governed by the laws of the State of [GOVERNING LAW STATE], without regard
     to its conflict of laws principles. The parties consent to venue in [VENUE].

14. GENERAL

14.1 This Agreement is the entire agreement between the parties on its subject matter and may be
     amended only in a writing signed by both parties. It prevails over any terms printed on a
     quotation, order acknowledgment, invoice or delivery note. Neither party may assign it without
     the other's prior written consent, except that either party may assign to a successor in
     connection with a merger or sale of substantially all assets, and the Flavor House shall
     ensure any successor assumes Sections 4, 7, 8 and 9. If any provision is held unenforceable,
     the remainder continues in effect. This Agreement may be signed in counterparts, including by
     electronic signature, each of which is an original.

IN WITNESS WHEREOF, the parties have executed this Agreement as of the Effective Date.


[COMPANY NAME]                            [FLAVOR HOUSE NAME]


By: _______________________________       By: _______________________________

Name: _____________________________       Name: _____________________________

Title: ____________________________       Title: ____________________________

Date: _____________________________       Date: _____________________________
`,
  },
];

export default PRODUCT_DEVELOPMENT_TEMPLATES;
